import type { ICustomerQuery } from "../queries/customer-gql";
import { useMemo } from "react";
import { extractId } from "../utils/common";
import { MEMBERSHIP_INFO } from "../utils/membership";

export const MEMBERSHIP_TIER = {
  khakis: ["REGULAR", "GREEN", "SILVER", "GOLD", "BLACK", "PLATINUM"],
  tune: ["WHITE", "GREEN", "SILVER", "GOLD", "PLATINUM", "BLACK"],
  thisisneverthat: ["MEMBER", "SILVER", "GOLD", "PLATINUM", "VIP"],
  sunlove: [],
  yeseyesee: ["WELCOME", "FRIENDS", "FAMILY", "PLATINUM", "VIP"],
  wellbeingexpress: ["LAYER0", "LAYER1", "LAYER2", "LAYER3", "LAYER4", "LAYER5"],
};

// 멤버십 할인 코드 사용이 불가능한 등급
export const NOT_AVAILABLE_MEMBERSHIP_TIER: Record<keyof typeof MEMBERSHIP_TIER, string[]> = {
  khakis: ["REGULAR", "GREEN"],
  tune: ["WHITE"],
  thisisneverthat: ["MEMBER"],
  sunlove: [],
  yeseyesee: ["WELCOME"],
  wellbeingexpress: ["LAYER0"],
};

export type TCustomerInfoStore = keyof typeof MEMBERSHIP_TIER;

const storageLang = (localStorage.getItem("i18nextLng") || "ko") as "ko" | "en" | "jp";
const lang = (storageLang === "jp" ? "en" : storageLang) as "ko" | "en";

export default function useCustomerInfo(
  customer: ICustomerQuery["customer"],
  store: "tune" | "thisisneverthat" | "khakis" | "sunlove" | "yeseyesee" | "wellbeingexpress",
) {
  const upperTags = useMemo(() => customer.tags.map((tag) => tag.toUpperCase()), [customer]);

  // 직원 / 지인 인지 판단
  const isEmployee = useMemo(
    () => ["EMPLOYEE", "FRIENDS", "SPECIAL"].some((tag) => upperTags.includes(tag)),
    [upperTags],
  );

  // 파트너사인지(토스 등)
  const isPartnership = useMemo(() => ["PARTNERSHIP_TOSS"].some((tag) => upperTags.includes(tag)), [upperTags]);

  const membership = useMemo(() => {
    if (upperTags.includes("EMPLOYEE")) return "EMPLOYEE";
    if (upperTags.includes("FRIENDS")) return "FRIENDS";

    // sunlove는 멤버십 없음!
    if (store === "sunlove") return "";

    const membershipTier = MEMBERSHIP_TIER[store];
    const defaultTier = membershipTier[0];

    const findMembershipTag = upperTags.find(
      (item) => item !== "MEMBERSHIP_CODE_USED" && item.startsWith("MEMBERSHIP_"),
    );
    if (findMembershipTag) {
      return findMembershipTag.replace("MEMBERSHIP_", "") || defaultTier;
    }

    const foundTag = upperTags.find((tag) => membershipTier.includes(tag));
    return foundTag ?? defaultTier;
  }, [upperTags, store]);

  // 멤버십 할인코드 표시를 위해 사용여부를 체크하는 변수
  const isMembershipDiscountCodeUsed = useMemo(() => customer.tags.includes("MEMBERSHIP_CODE_USED"), [customer]);

  // 주소 목록
  const addresses = useMemo(() => {
    if (!customer.defaultAddress) return [];

    return customer.addresses.edges
      .sort(({ node }) => (extractId(node.id) === extractId(customer.defaultAddress?.id || "") ? -1 : 1))
      .map(({ node }) => node);
  }, [customer]);

  // 할인코드 사용 가능한 멤버십 등급
  const codeUsableMembership = useMemo(() => {
    const notAvailableTiers = NOT_AVAILABLE_MEMBERSHIP_TIER[store];
    return !notAvailableTiers.includes(membership);
  }, [membership, store]);

  // 소셜 로그인 계정인지
  const isSocialAccount = useMemo(
    () => customer.tags.includes("hiko") || customer.tags.includes("Social") || customer.tags.includes("social"),
    [customer],
  );

  // 이메일인지 소셜 로그인인지
  const socialLoginType = useMemo(() => {
    const socialTag = customer.tags.find((item) => item.includes("social"));
    return socialTag ? socialTag.replace("social:", "").replace("social_", "") : "";
  }, [customer]);

  const isDeleteRequest = useMemo(() => customer.tags.includes("탈퇴요청"), [customer]);

  const membershipCalcDate = useMemo(() => {
    const today = new Date();

    // 주어진 날짜 기준, 다음 달 1일
    const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    // 주어진 날짜 기준, 정확히 1년 전 날짜
    const oneYearAgo = new Date(nextMonthStart.getFullYear() - 1, nextMonthStart.getMonth(), nextMonthStart.getDate());

    // 주어진 날짜 기준, 정확히 3년 전 날짜
    const threeYearsAgo = new Date(
      nextMonthStart.getFullYear() - 3,
      nextMonthStart.getMonth(),
      nextMonthStart.getDate(),
    );

    // 주어진 날짜 기준, 해당 월의 마지막 날에서 6일을 뺀 날짜
    const lastDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const oneWeekBeforeMonthEnd = new Date(today.getFullYear(), today.getMonth(), lastDayOfCurrentMonth - 6, 0, 0, 0);

    return { startDate: store === "thisisneverthat" ? threeYearsAgo : oneYearAgo, endDate: oneWeekBeforeMonthEnd };
  }, [store]);

  const membershipList = useMemo(() => MEMBERSHIP_INFO[store][lang], [store]);

  const membershipInfo = useMemo(
    () => MEMBERSHIP_INFO[store][lang].find((item) => item.title.toUpperCase() === membership),
    [store, membership],
  );

  return {
    isEmployee: isEmployee || isPartnership,
    isPartnership,
    membership,
    isMembershipDiscountCodeUsed,
    addresses,
    codeUsableMembership,
    isSocialAccount,
    socialLoginType,
    isDeleteRequest,
    membershipCalcDate,
    membershipList,
    membershipInfo,
  };
}
